// The PENDING-QUESTION store — what makes a question Otto ASKED part of the conversation's state instead
// of a sentence that stops existing the moment it renders. Generalized (2026-07-27) from the capability-
// redirect-only pendingClarifyStore: EVERY question Otto asks with a concrete answer space registers here,
// so the user's next message can be resolved AGAINST THE QUESTION rather than re-entering the pipeline as
// a cold, context-free utterance.
//
// The bug class it closes ("Otto is robotic"): Otto asks "Which Region did you mean — CFD, EFD, or WFD?",
// the user answers "all of them", and the reply goes back through the classifier + string grounding, which
// can never match a reply that refers to the QUESTION rather than to an option — so Otto asks the same
// question again. History alone can't fix this: the model sees the prior turns and still captures a value
// phrase for grounding to fail on. The question and its answer space must be carried in state.
//
// Entry shape (see questionFromProposal.js for how proposals map to it):
//   questionId  — the turn's request/plan id (tracing + at-most-once)
//   kind        — resolution strategy: "redirect_choice" | "page_choice" | "table_changes" |
//                 "table_values" | "table_column" | "field_options" (browse card, FTO-10) |
//                 "field_threshold" (numeric prompt card, FTO-12 — answered by a pre-matcher hook)
//                 (FilterAssistant.runQuestionResolution dispatches on it)
//   page        — the page the question was asked on (its premise is page-specific)
//   utterance   — the ORIGINAL request that led to the question (so answering can't lose it)
//   question    — the text Otto asked (tracing/history)
//   options     — [{ id, label, ...kind-specific payload (steps / changes) }]
//   multi       — whether the answer may pick several options ("all of them" is only meaningful here)
//   payload     — kind-specific context (e.g. the column a value clarify is about)
//
// Safety properties, mirroring pendingPlanStore (persisted intent that fires later is dangerous stale):
//   - Written ONLY when Otto actually asks; never speculatively.
//   - TTL'd (3 minutes): a question answered much later is not an answer, it's a new request.
//   - Bound to the PAGE it was asked on.
//   - ONE-SHOT: the caller clears on the next submit whether or not it matched.
//   - localStorage (like the chat), self-cleaning on any invalid/expired read. Same key as the store it
//     replaces — old-shape entries fail validation and self-clean.
const KEY = "fai.pendingClarify";
export const QUESTION_TTL_MS = 3 * 60 * 1000;

const storage = () => {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch (e) {
        return null; // absent/blocked — degrades to "typed answers aren't resolved", never to a crash
    }
};

export const clearQuestion = () => {
    const s = storage();
    if (!s) return;
    try {
        s.removeItem(KEY);
    } catch (e) {
        /* ignore */
    }
};

// Record the question Otto just asked. `options` are the SAME entries any buttons/chips render, so the
// typed path and the tapped path can only ever do the same thing.
export const saveQuestion = ({ questionId, kind, page, utterance, question, options, multi, payload }) => {
    const s = storage();
    if (!s || !kind || !page || !Array.isArray(options) || !options.length) return null;
    const entry = {
        questionId: questionId || null,
        kind,
        page,
        utterance: utterance || "",
        question: question || "",
        options,
        multi: !!multi,
        payload: payload || null,
        askedAt: Date.now()
    };
    try {
        s.setItem(KEY, JSON.stringify(entry));
    } catch (e) {
        return null; // quota/blocked — the buttons still work, the typed path just won't resolve
    }
    return entry;
};

// The live question for THIS page, or null. Self-cleans expired/malformed entries (including entries
// written by the pre-generalization store, which lack `kind`).
export const loadQuestion = page => {
    const s = storage();
    if (!s) return null;
    let entry = null;
    try {
        const raw = s.getItem(KEY);
        entry = raw ? JSON.parse(raw) : null;
    } catch (e) {
        entry = null;
    }
    if (!entry) return null;
    if (!entry.kind || !entry.page || !Array.isArray(entry.options) || !entry.options.length || !entry.askedAt) {
        clearQuestion();
        return null;
    }
    if (Date.now() - entry.askedAt > QUESTION_TTL_MS) {
        clearQuestion();
        return null;
    }
    if (page && entry.page !== page) return null; // asked elsewhere — leave it for that page, don't answer it here
    return entry;
};
